/**
 * ThreadHeader — the chat view's header: back to inbox, avatar/handle/pills,
 * the subject + mailbox line, prev/next through the current page of rows,
 * and the thread's own "⋯" menu (Task 24) — a DISTINCT item set from the
 * row menu Tasks 22/23 built (RowActions.tsx): no View profile/Open thread
 * (already looking at this thread), no Suppress/Remove from suppression
 * (ruling 52 kept those on the row menu only). Every item calls the matching
 * useOutreachEntryActions mutation one level up (ThreadView → OutreachTab),
 * the same handlers/toast copy RowActions already uses, so the action reads
 * identically no matter which menu triggered it.
 */
import type { OutreachCloseReason, OutreachRow } from '@discovr/types';
import { followupPauseAction } from './followupPauseUi';
import { isOutreachProgress, isWithBrandStatus } from '@discovr/types';
import {
  ArrowLeft,
  ArrowCounterClockwise,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CheckCircle,
  DotsThreeVertical,
  Envelope,
  HandCoins,
  Pause,
  Play,
  Trash,
  User,
  XCircle,
} from '../../../design-system/icons';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { OutreachRowPills } from './OutreachRowPills';
import { PILL_BASE } from './OutreachStatusPill';
import { PILL_INDIGO } from './pillPalette';
import { canAgreeRate, canNotUnsubscribe, canReopenRow, hasRateToHandOff, isInOutreach, isRemovedRow } from './handoffStates';
import { CLOSE_REASON_LABELS } from './sendLabels';

function initials(row: OutreachRow): string {
  return (row.displayName || row.username)
    .split(' ')
    .map((word) => word[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const ORDINARY_CLOSE_REASONS: OutreachCloseReason[] = ['rejected', 'not_interested', 'ghosted'];

/** A brand-declined hand-off closes with its own reason only (D6); everything else keeps the three ordinary ones. */
function closeReasonsFor(row: OutreachRow): [OutreachCloseReason, string][] {
  const reasons: OutreachCloseReason[] = isWithBrandStatus(row.status) ? ['brand_rejected'] : ORDINARY_CLOSE_REASONS;
  return reasons.map((reason) => [reason, CLOSE_REASON_LABELS[reason]]);
}

export interface ThreadHeaderProps {
  row: OutreachRow;
  subject?: string;
  /** A message has gone out (or come in): only then is the subject a reply, so only then does it read "Re:". */
  hasSent?: boolean;
  campaignZone?: string;
  autoFollowups?: boolean;
  canWrite: boolean;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onBack: () => void;
  onOpenProfile: () => void;
  /** Whether the inline profile panel is currently shown — flips this
   *  button's label between 'Creator profile' and 'Hide profile' (Figma). */
  profileOpen?: boolean;
  onMarkUnread: () => void;
  onPauseFollowups: () => void;
  onResumeFollowups: () => void;
  onClose: (reason: OutreachCloseReason) => void;
  onReopen: () => void;
  onNotUnsubscribe: () => void;
  onSendToBrand: () => void;
  onRemove: () => void;
  onAgreeRate: () => void;
  onClearEscalation?: () => void;
}

/** The thread's subject without any reply/forward prefixes, so the header shows exactly one "Re:" (Figma). */
export const baseSubject = (subject: string): string => subject.replace(/^\s*(?:(?:re|fwd?)\s*:\s*)+/i, '');

export function ThreadHeader({
  row,
  subject,
  hasSent = true,
  campaignZone,
  autoFollowups,
  canWrite,
  canPrev,
  canNext,
  onPrev,
  onNext,
  onBack,
  onOpenProfile,
  profileOpen = false,
  onMarkUnread,
  onPauseFollowups,
  onResumeFollowups,
  onClose,
  onReopen,
  onNotUnsubscribe,
  onSendToBrand,
  onRemove,
  onAgreeRate,
  onClearEscalation,
}: ThreadHeaderProps) {
  const name = (row.displayName || row.username).trim();
  const firstName = name.split(/\s+/)[0] || name;
  const base = subject ? baseSubject(subject).trim() : '';
  const pauseAction = followupPauseAction(row);
  const paused = pauseAction === 'resume';
  const withBrand = isWithBrandStatus(row.status);
  const inOutreach = isInOutreach(row);
  const canClose = inOutreach ? isOutreachProgress(row.outreachState) : withBrand && row.brandState === 'rejected';
  const canReopen = canReopenRow(row);

  return (
    // Figma 505:50286 "Thread Header": full-width white bar, 24/8 padding, 12px gap, bottom border.
    <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-6 py-2">
      <button
        type="button"
        aria-label="Back to inbox"
        onClick={onBack}
        className="flex size-6 shrink-0 items-center justify-center rounded-md text-foreground hover:bg-muted outline-none focus-visible:ring-2 focus-visible:ring-foreground"
      >
        <ArrowLeft size={20} />
      </button>
      <Avatar size="lg">
        {row.avatarUrl && <AvatarImage src={row.avatarUrl} alt="" />}
        <AvatarFallback>{initials(row)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        {/* Figma 505:50187 Name Row: the name, then one line of 24px pills. */}
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="m-0 truncate text-[18px] leading-[26px] font-medium text-foreground">{name}</h1>
          <div data-testid="header-pills" className="flex">
            <OutreachRowPills row={row} campaignZone={campaignZone} autoFollowups={autoFollowups} nameStopReason />
          </div>
          {/* Replied: the state pill already says "Quoted rate". rate_provided or past hand-off: the rate is agreed, so a
              quote is no longer news. Past hand-off the stage is the roster status, never outreach_state (owner ruling). */}
          {row.lastReply?.intent === 'quoted_rates' && inOutreach && row.outreachState !== 'replied' && row.outreachState !== 'rate_provided' && (
            <span className={cn(PILL_BASE, PILL_INDIGO)}>Quoted rate</span>
          )}
        </div>
        <p className="mt-1 truncate text-caption-regular/4 text-muted-foreground">
          {`${base ? `${hasSent ? 'Re: ' : ''}${base} — ` : ''}${firstName} · @${row.username}`}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous thread"
          disabled={!canPrev}
          onClick={onPrev}
          className="flex size-8 items-center justify-center rounded-lg text-foreground hover:bg-muted outline-none focus-visible:ring-2 focus-visible:ring-foreground disabled:opacity-40"
        >
          <CaretLeft size={16} />
        </button>
        <button
          type="button"
          aria-label="Next thread"
          disabled={!canNext}
          onClick={onNext}
          className="flex size-8 items-center justify-center rounded-lg text-foreground hover:bg-muted outline-none focus-visible:ring-2 focus-visible:ring-foreground disabled:opacity-40"
        >
          <CaretRight size={16} />
        </button>
        </div>
        <Button variant="outline" size="lg" className="border-border focus-visible:border-border focus-visible:ring-2 focus-visible:ring-foreground" onClick={onOpenProfile}>
          <User size={16} />
          {profileOpen ? 'Hide profile' : 'Creator profile'}
        </Button>
        {canWrite ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More actions"
                className="flex size-8 items-center justify-center rounded-lg text-foreground hover:bg-muted outline-none focus-visible:ring-2 focus-visible:ring-foreground"
              >
                <DotsThreeVertical size={24} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!row.unread && (
                <DropdownMenuItem onSelect={onMarkUnread}>
                  <Envelope size={14} />
                  Mark as unread
                </DropdownMenuItem>
              )}
              {pauseAction && (
                <DropdownMenuItem onSelect={paused ? onResumeFollowups : onPauseFollowups}>
                  {paused ? <Play size={14} /> : <Pause size={14} />}
                  {paused ? 'Resume follow-ups' : 'Pause follow-ups'}
                </DropdownMenuItem>
              )}
              {canAgreeRate(row) && (
                <DropdownMenuItem onSelect={onAgreeRate}>
                  <HandCoins size={14} />
                  Agree rate…
                </DropdownMenuItem>
              )}
              {row.escalatedAt && onClearEscalation && (
                <DropdownMenuItem onSelect={onClearEscalation}>
                  <CheckCircle size={14} />
                  Clear escalation
                </DropdownMenuItem>
              )}
              {canClose && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <XCircle size={14} />
                    Close conversation
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {closeReasonsFor(row).map(([value, label]) => (
                      <DropdownMenuItem key={value} onSelect={() => onClose(value)}>
                        {label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              {canNotUnsubscribe(row) && (
                <DropdownMenuItem onSelect={onNotUnsubscribe}>
                  <Envelope size={14} />
                  Not an unsubscribe
                </DropdownMenuItem>
              )}
              {canReopen && (
                <DropdownMenuItem onSelect={onReopen}>
                  <ArrowCounterClockwise size={14} />
                  Reopen
                </DropdownMenuItem>
              )}
              {hasRateToHandOff(row) && (
                <DropdownMenuItem onSelect={onSendToBrand}>
                  <ArrowRight size={14} />
                  Move to brand dashboard
                </DropdownMenuItem>
              )}
              {!withBrand && !isRemovedRow(row) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onRemove}>
                    <Trash size={14} />
                    Remove from campaign
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <button
            type="button"
            aria-label="More actions"
            disabled
            title="You can read this thread but not act on it."
            className="flex size-8 items-center justify-center rounded-lg text-foreground opacity-40"
          >
            <DotsThreeVertical size={24} />
          </button>
        )}
      </div>
    </header>
  );
}
